import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AcknowledgeModal,
  TrackingCarrierBadge,
  TrackingNotePreview,
  TrackingOrderTime,
  TrackingStatusBadge,
  TrackingNumberContainer,
  type ITrackingItem,
} from '@page-components/order/tracking';
import {
  OrganisationAvatar,
  TextCopyToClipboard,
  type IFilterConfig,
} from '@components/index';
import { TrackingHook, useDebouncedMemo } from '@hooks/index';
import {
  CommonConstant,
  CommonUtility,
  DateFormat,
  DateUtility,
  theme,
} from '@utility/index';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BoundingBox, Truck } from '@phosphor-icons/react';
import { useOrdersTabExtraContent } from '@contexts/orders-tab-extra';
import { OrdersTabToolbar } from '@page-components/order/orders-tab-toolbar';
import { Table } from 'antd';
import { HoverBorderButton, MuteText } from '@elements/index';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { TableProps } from 'antd';
import type { TSortDirection } from 'types';

function getAntdSortOrder(sortDirection?: string) {
  if (sortDirection === CommonConstant.sortDirection.ascending) {
    return 'ascend';
  }
  if (sortDirection === CommonConstant.sortDirection.descending) {
    return 'descend';
  }
  return null;
}

function getSortDirection(order: 'ascend' | 'descend'): TSortDirection {
  return order === 'ascend'
    ? (CommonConstant.sortDirection.ascending as TSortDirection)
    : (CommonConstant.sortDirection.descending as TSortDirection);
}

function getAcknowledgedByName(item: ITrackingItem) {
  if (!item.acknowledgedBy) return 'N/A';

  return (
    `${item.acknowledgedBy.first_name || ''} ${
      item.acknowledgedBy.last_name || ''
    }`.trim() ||
    item.acknowledgedBy.email ||
    'Unknown'
  );
}

export function TrackingTab(): JSX.Element {
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState('');

  const [intermediateLoading, setIntermediateLoading] = useState<boolean>(false);

  const {
    loading,
    data,
    totalData,
    pageChanged,
    parsedSort,
    filter: tableFilter,
    filterChanged,
    onSortChange,
    refresh,
    updateAllFilter,
  } = TrackingHook({
    customerId,
  });

  useEffect(() => {
    setIntermediateLoading(loading);
  }, [loading]);

  const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState<boolean>(false);
  const [selectedTracking, setSelectedTracking] = useState<ITrackingItem | null>(null);

  const createdAtSortOrder = getAntdSortOrder(parsedSort.createdAt);

  const openAcknowledgeModal = useCallback((tracking: ITrackingItem) => {
    setSelectedTracking(tracking);
    setAcknowledgeModalOpen(true);
  }, []);

  const closeAcknowledgeModal = useCallback(() => {
    setAcknowledgeModalOpen(false);
    setSelectedTracking(null);
  }, []);

  const handleAcknowledgeSuccess = useCallback(() => {
    closeAcknowledgeModal();
    refresh();
  }, [closeAcknowledgeModal, refresh]);

  const showAcknowledgeButton = useMemo(
    () => tableFilter.category !== 'ignored',
    [tableFilter.category],
  );

  const trackingColumns: ColumnsType<ITrackingItem> = useMemo(() => {
    const commonColumns: ColumnsType<ITrackingItem> = [
      {
        title: 'Created At',
        key: 'createdAt',
        align: 'left',
        width: '15%',
        sorter: true,
        sortOrder: createdAtSortOrder,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, item) =>
          item.label?.createdAt ? (
            <>
              <TrackingOrderTime time={item.label.createdAt} isEnabled />
              <div>
                <MuteText>
                  {DateUtility.formatDate(item.label.createdAt, DateFormat.barChartMonth)}
                </MuteText>
              </div>
            </>
          ) : null,
      },
      {
        title: 'Tracking Number',
        key: 'trackingNumber',
        align: 'left',
        width: '16%',
        render: (_, item) => (
          <TrackingNumberContainer>
            <Link to={item.link} target="_blank">
              {item.trackingNumber}
            </Link>
            <TextCopyToClipboard text={item.trackingNumber} showText={false} showIcon />
          </TrackingNumberContainer>
        ),
      },
      {
        title: 'Order',
        key: 'marketplaceOrderDisplayId',
        align: 'left',
        width: '12%',
        render: (_, item) => {
          const providerId = item.order?.fulfillmentProviderId || '';
          const orderDisplayId = item.order?.marketplaceOrderDisplayId || 'N/A';
          const orderId = item.order?._id || '';

          return orderId ? (
            <Link to={`/app/fulfill/orders/details/${orderId}/?providerId=${providerId}`}>
              {orderDisplayId}
            </Link>
          ) : (
            <span>{orderDisplayId}</span>
          );
        },
      },
      {
        title: 'Customer',
        key: 'organization',
        align: 'left',
        width: '16%',
        render: (_, item) => (
          <div className="d-flex justify-content-start align-items-center">
            <OrganisationAvatar
              image={item.order?.organizationLogo?.url}
              name={item.order?.organizationName}
            />
            <span className="text-truncate">&nbsp;{item.order?.organizationName}</span>
          </div>
        ),
      },
      {
        title: 'Carrier',
        key: 'carrier',
        align: 'center',
        width: '10%',
        render: (_, item) => <TrackingCarrierBadge carrier={item.carrier} />,
      },
      {
        title: 'Status',
        key: 'status',
        align: 'left',
        width: '9%',
        render: (_, item) => (
          <TrackingStatusBadge
            message={item.message}
            category={item.category}
            status={item.status}
          />
        ),
      },
    ];

    if (tableFilter.category === 'ignored') {
      return [
        ...commonColumns,
        {
          title: 'Acknowledged By',
          align: 'left',
          key: 'acknowledgedBy',
          width: '12%',
          render: (_, item) => getAcknowledgedByName(item),
        },
        {
          title: 'Acknowledged At',
          align: 'left',
          key: 'acknowledgedAt',
          width: '12%',
          render: (_, item) =>
            item.acknowledgedAt
              ? DateUtility.formatDate(item.acknowledgedAt, 'MMM DD, YYYY HH:mm')
              : 'N/A',
        },
        {
          title: 'Note',
          align: 'left',
          key: 'acknowledgedNote',
          width: '12%',
          render: (_, item) => <TrackingNotePreview note={item.acknowledgedNote} />,
        },
      ];
    }

    return [
      ...commonColumns,
      {
        title: '',
        key: 'actions',
        align: 'center',
        fixed: 'right',
        width: 130,
        render: (_, item) =>
          showAcknowledgeButton && item.category === 'alerts' && !item.acknowledged ? (
            <HoverBorderButton
              className="text-nowrap"
              onClick={() => openAcknowledgeModal(item)}
            >
              Acknowledge
            </HoverBorderButton>
          ) : null,
      },
    ];
  }, [
    createdAtSortOrder,
    openAcknowledgeModal,
    showAcknowledgeButton,
    tableFilter.category,
  ]);

  const [searchParams] = useSearchParams();

  const categoryOptions = [
    { value: 'in_process_of_delivery', text: 'Delivering' },
    { value: 'alerts', text: 'Alerts' },
    { value: 'ignored', text: 'Ignored' },
  ];

  const carrierOptions = [
    { value: 'ups', text: 'UPS' },
    { value: 'usps', text: 'USPS' },
    { value: 'fedex', text: 'fedEx' },
    { value: 'dhl', text: 'DHL' },
    { value: 'uniuni', text: 'UniUni' },
  ];

  const filters: IFilterConfig[] = useDebouncedMemo(
    () => {
      const query = Object.fromEntries(searchParams.entries());
      const temp: Record<string, unknown> = { ...query };
      Object.keys(temp).forEach(key => {
        const final = CommonUtility.tryParseJSONObject(String(temp[key]));
        if (final) {
          temp[key] = final;
        }
      });

      const defaultValues = temp as Record<
        string,
        string | number | string[] | undefined
      >;

      const arr: IFilterConfig[] = [
        {
          name: 'category',
          label: 'Category',
          icon: <BoundingBox />,
          options: categoryOptions,
          defaultValue: (defaultValues && defaultValues.category) || 'alerts',
          ignoreDefault: 'alerts',
        },
        {
          name: 'carrier',
          label: 'Carrier',
          icon: <Truck />,
          options: carrierOptions,
          defaultValue: defaultValues && defaultValues.carrier,
        },
        {
          skip: true,
          name: 'page',
          must: true,
          defaultValue: (defaultValues && defaultValues.page) || 1,
          ignoreDefault: 1,
        },
        {
          skip: true,
          name: 'limit',
          must: true,
          defaultValue:
            defaultValues && CommonUtility.isValidNumber(defaultValues.limit)
              ? Number(defaultValues.limit)
              : 50,
          ignoreDefault: 50,
        },
      ];

      return arr;
    },
    [searchParams],
    500,
    [],
  );

  const setFilter = useCallback(
    (next: Record<string, unknown>) => {
      updateAllFilter({ ...next, page: 1 } as Partial<typeof tableFilter>);
    },
    [updateAllFilter],
  );

  const didHydrateFromUrl = useRef(false);

  useEffect(() => {
    if (didHydrateFromUrl.current) return;
    didHydrateFromUrl.current = true;

    const query = Object.fromEntries(searchParams.entries());
    if (Object.keys(query).length) {
      updateAllFilter(query as Partial<typeof tableFilter>);
    }
  }, [searchParams, updateAllFilter]);

  useEffect(() => {
    const urlObject: Record<string, string> = {};
    Object.keys(tableFilter)?.forEach(key => {
      const value = tableFilter[key as keyof typeof tableFilter];
      const config = filters?.find(f => f.name === key);
      if (key === 'sort') {
        const sortValue = JSON.stringify(value);
        if (sortValue !== JSON.stringify(config?.ignoreDefault)) {
          urlObject[key] = sortValue;
        }
      } else if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        config?.ignoreDefault !== value
      ) {
        urlObject[key] = String(value);
      }
    });
    const nextSearch = new URLSearchParams(urlObject).toString();
    if (searchParams.toString() === nextSearch) return;

    navigate({ search: nextSearch }, { replace: true });
  }, [tableFilter, filters, navigate, searchParams]);

  useOrdersTabExtraContent(
    () => (
      <OrdersTabToolbar
        filters={filters}
        filter={tableFilter}
        setFilter={setFilter}
        filterChanged={filterChanged}
        onChangeCustomer={setCustomerId}
        placeholder="Search by Tracking Number, Order Id"
      />
    ),
    [filters, tableFilter, setFilter, filterChanged, setCustomerId],
  );

  const tablePagination: TablePaginationConfig = useMemo(
    () => ({
      current: Number(tableFilter.page || 1),
      pageSize: Number(tableFilter.limit || 50),
      pageSizeOptions: CommonConstant.pageSizes.map(option => option.value),
      showSizeChanger: true,
      showTotal: total => `${CommonUtility.numberWithCommas(total)} tracking`,
      total: totalData,
    }),
    [tableFilter.limit, tableFilter.page, totalData],
  );

  const handleTableChange: TableProps<ITrackingItem>['onChange'] = useCallback(
    (pagination, _, sorter, extra) => {
      if (extra?.action === 'sort') {
        const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        if (!nextSorter?.order) return;

        onSortChange({
          createdAt: getSortDirection(nextSorter.order),
        });
        return;
      }

      const nextPageSize = Number(pagination?.pageSize || 50);
      const currentPageSize = Number(tableFilter.limit || 50);
      if (nextPageSize !== currentPageSize) {
        filterChanged('limit', nextPageSize);
        return;
      }

      const nextPage = Number(pagination?.current || 1);
      if (nextPage !== Number(tableFilter.page || 1)) {
        pageChanged(nextPage);
      }
    },
    [filterChanged, onSortChange, pageChanged, tableFilter.limit, tableFilter.page],
  );

  return (
    <div>
      <Table<ITrackingItem>
        columns={trackingColumns}
        loading={intermediateLoading}
        dataSource={data}
        onChange={handleTableChange}
        pagination={tablePagination}
        rowKey={(item, index) =>
          item.label?._id || `${item.carrier}-${item.trackingNumber}-${index}`
        }
        scroll={{ x: 1100 }}
        size="small"
        tableLayout="fixed"
        style={{
          borderRadius: 6,
          border: `1px solid ${theme.colors.semanticBorder}`,
        }}
      />
      {acknowledgeModalOpen && selectedTracking && (
        <AcknowledgeModal
          open={acknowledgeModalOpen}
          onClose={closeAcknowledgeModal}
          tracking={selectedTracking}
          onSuccess={handleAcknowledgeSuccess}
        />
      )}
    </div>
  );
}
